/**
 * Every prompt in the debate, in one editable place.
 *
 * WHY THIS EXISTS. Free's call, 2026-07-27: "Lets bypass the fine tuning of
 * prompts for the moment. Lets get the UI up and running and expose all the
 * prompts for me to play around with and find good ways to snap them into
 * coherence in a debate."
 *
 * That is the right division of labour and the measurements say so. A single
 * prompt A/B costs about five minutes of model time here, and the last three
 * taught: the banner was overriding the think prompt, hedging the wording made
 * every small model fabricate, and the opener template is a starvation
 * signature rather than a protocol one. Each of those was a five-minute run to
 * learn something a person watching a transcript spots at once. The person
 * flying it should hold the knobs.
 *
 * THE DEFAULTS ARE PINNED BYTE FOR BYTE. `tools/test/promptKit.test.mjs`
 * compares every rendered default against a literal copy of the string the
 * hand-written functions produced before this file existed. Exposing the
 * prompts must not change what they say, or every measurement recorded in
 * JOURNAL.md silently stops describing the running system.
 *
 * A SPEC RATHER THAN A BARE MAP. The UI renders itself from `PROMPT_SPECS`,
 * so a prompt added here appears in the panel with its help text and its
 * placeholder list and needs no UI change at all. `missingVars` exists because
 * the first thing anyone does with an editable prompt is delete a placeholder
 * by accident, and `{question}` going missing would quietly produce a debate
 * about nothing.
 */

/** Which stage of the protocol a prompt belongs to. Groups the UI panel. */
export type PromptStage =
  | "system"
  | "agenda"
  | "prepare"
  | "opening"
  | "rebuttal"
  | "memory"
  | "token";

/**
 * What the two speakers are trying to do to each other.
 *
 * Free's idea: "with the right prompts and adjustments you can set 2 experts up
 * to collaborate on a topic not just debate it."
 *
 * It is a better fit for the lattices that actually exist. Debate needs two
 * memories with purchase on ONE subject, and measured, no two packs overlap:
 * Spark is compute, Webdev is web, Printer is printing, and Gardener and
 * Hermeticist share only a thin 30-fact elements bridge. Collaboration needs
 * the opposite, memories that COMPLEMENT, and difference is the one thing this
 * catalogue has plenty of.
 */
export type DebateMode = "debate" | "collaborate";

export type PromptId =
  | "system"
  | "recordLabel"
  | "agenda"
  | "agendaCollab"
  | "introCollab"
  | "openingCollab"
  | "rebuttalThinkCollab"
  | "rebuttalCollab"
  | "intro"
  | "question"
  | "opening"
  | "rebuttalThink"
  | "rebuttal"
  | "handoff"
  | "rest"
  | "bannerConfident"
  | "bannerDeliberate"
  | "bannerHedged";

export type PromptSpec = {
  id: PromptId;
  label: string;
  stage: PromptStage;
  /** What it does and when it fires. Shown in the panel. */
  help: string;
  /** Placeholders this prompt may use, without braces. */
  vars: string[];
  /** Placeholders it MUST keep, or the stage breaks. Subset of `vars`. */
  required: string[];
  /**
   * Which mode this prompt belongs to. Absent means it is used by both.
   *
   * The panel filters on this, so an operator tuning a collaboration is not
   * scrolling past five prompts that are not in play. The registry stays flat;
   * only the view narrows.
   */
  mode?: DebateMode;
  text: string;
};

/**
 * Substitute `{name}` placeholders.
 *
 * Unknown placeholders are left alone rather than blanked. A typo should look
 * like a typo in the output, not vanish into an empty string where it reads as
 * a missing sentence.
 */
export function render(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : whole,
  );
}

/** Required placeholders this text has lost. Empty means it is safe to use. */
export function missingVars(spec: PromptSpec, text: string): string[] {
  return spec.required.filter((v) => !text.includes(`{${v}}`));
}

/** Placeholders present in the text that the stage will never supply. */
export function unknownVars(spec: PromptSpec, text: string): string[] {
  const found = [...text.matchAll(/\{(\w+)\}/g)].map((m) => m[1]);
  return [...new Set(found.filter((v) => !spec.vars.includes(v)))];
}

export const PROMPT_SPECS: PromptSpec[] = [
  {
    id: "system",
    label: "System prompt (wraps each persona)",
    stage: "system",
    help:
      "A WRAPPER, not a replacement. `{preamble}` is the persona's own text, baked into its .rlm and different " +
      "for every lattice, which is what makes two speakers disagree. Replacing it outright would make both sides " +
      "the same voice and destroy the point of the bay. Add rules around it instead: put text before or after " +
      "`{preamble}` and it applies to every persona while each keeps its own character.",
    vars: ["preamble", "persona"],
    required: ["preamble"],
    text: "{preamble}",
  },
  {
    id: "recordLabel",
    label: "Memory record label (the word before each fact)",
    stage: "memory",
    help:
      "THE LABEL IS THE INSTRUCTION. Measured in Pocket Lattice: identical records scored .97 with meaningful " +
      "keys against .11 with one-letter keys, same windows, same instruction, and LLaDA answered `f: g: h: i:` " +
      "because it read a meaningless label as a sequence to continue. That was measured on a model FILLING slots " +
      "rather than reading them, so applying it here is an extrapolation, which is exactly why it is a dial " +
      "instead of a decision. One short phrase, no colon: the colon is added for you.",
    vars: [],
    required: [],
    text: "relevant memory",
  },
  {
    id: "agenda",
    label: "Agenda: propose questions",
    stage: "agenda",
    mode: "debate",
    help:
      "Runs once per side before any debating. Cheapest thing in the flow, and it doubles as a smoke test: " +
      "a side that cannot produce a coherent question will not produce a coherent argument. Retrieval strength " +
      "here is what warns the visitor about a lopsided pairing.",
    vars: ["other", "topic", "n"],
    required: ["topic"],
    text: [
      `You are about to debate {other} about: {topic}`,
      ``,
      `Propose {n} questions the two of you should argue over. Draw them from what`,
      `you actually know about this, so pick questions you could argue well.`,
      `One question per line, nothing else.`,
    ].join("\n"),
  },
  {
    id: "intro",
    label: "Staging think: who you are up against",
    stage: "prepare",
    mode: "debate",
    help:
      "Once per debate per speaker, before any question. Asks for STANCE and forbids figures, because this pass " +
      "runs WITHOUT retrieval and a pass with nothing to check against invents specifics: it produced 'roughly " +
      "67% of the arable land' from nowhere. Numbers belong to the per-question think, where they can be contradicted.",
    vars: ["other", "topic", "order"],
    required: ["topic"],
    text: [
      `You are in a debate with {other} about: {topic}`,
      ``,
      `You go {order}. Take a moment to prepare. Say what`,
      `your tradition holds on this and where you expect to disagree with them.`,
      ``,
      `Your position only, in general terms. Do not cite figures, percentages or`,
      `measurements here; those come later, once you have looked things up. Do not`,
      `start debating yet.`,
    ].join("\n"),
  },
  {
    id: "question",
    label: "Per-question think",
    stage: "prepare",
    help:
      "Once per question per speaker. This one DOES retrieve, so it is where figures are allowed. Its output is " +
      "prefilled into the assistant turn, which means the answer continues this note rather than replying to it.",
    vars: ["question", "n"],
    required: ["question"],
    text: [
      `Question {n} for the debate:`,
      ``,
      `"{question}"`,
      ``,
      `Prepare your answer to this specific question. Note what you know that`,
      `bears on it, and look up anything you are unsure of. Do not answer yet.`,
    ].join("\n"),
  },
  {
    id: "opening",
    label: "Opening statement instruction",
    stage: "opening",
    mode: "debate",
    help:
      "'Nobody has spoken yet' is load-bearing, not decoration. Without it the second speaker opened 'That's a " +
      "compelling observation!', reacting to a phantom, because a prefilled continuation reads as mid-conversation. " +
      "In this round an agent never sees the opponent's answer, which is what makes the openings independent.",
    vars: [],
    required: [],
    text: [
      `This is your opening statement. Nobody has spoken yet, so do not react to`,
      `anyone. State your own position on the question, in your own voice, from`,
      `what you know. A few sentences.`,
    ].join("\n"),
  },
  {
    id: "rebuttalThink",
    label: "Rebuttal think: read them, find the fault",
    stage: "rebuttal",
    mode: "debate",
    help:
      "The opponent's answer appears here so the agent can react to it, but the RETRIEVAL that follows is driven " +
      "by the resulting note plus the question, never by their raw text. Searching your own lattice with the " +
      "opponent's words is what produced the original paraphrasing bug.",
    vars: ["other", "question", "theirAnswer"],
    required: ["theirAnswer"],
    text: [
      `The question was: "{question}"`,
      ``,
      `{other} said:`,
      ``,
      `{theirAnswer}`,
      ``,
      `Note to yourself where they are wrong or incomplete, and what you know that`,
      `shows it. Look anything up that you need. Do not reply to them yet.`,
    ].join("\n"),
  },
  {
    id: "rebuttal",
    label: "Rebuttal instruction",
    stage: "rebuttal",
    mode: "debate",
    help:
      "NO QUOTATION MARKS around the opponent's turn anywhere in this stage. Handing a speaker the opponent's " +
      "answer inside quotes taught it that a reply LOOKS like a quoted paragraph, and by the third exchange one " +
      "speaker reproduced the other's attack on itself in the third person. 3 flagged turns to 0 once removed.",
    vars: ["rest"],
    required: ["rest"],
    text: [
      `Reply to them in your OWN words, from your own tradition. Say where they are`,
      `wrong and why. Do not restate their argument back to them.`,
      ``,
      `If you have nothing of your own to add, say only: {rest}`,
    ].join("\n"),
  },
  /* ---- collaboration: the same protocol, aimed the other way ---- */

  {
    id: "agendaCollab",
    label: "Agenda: propose questions (collaborate)",
    stage: "agenda",
    mode: "collaborate",
    help:
      "Same fitness probe as the debate version. The difference is what makes a good question: not one the two of " +
      "them will disagree about, but one neither could answer alone. Retrieval strength here still warns about a " +
      "side with nothing to contribute.",
    vars: ["other", "topic", "n"],
    required: ["topic"],
    text: [
      `You are about to work with {other} on: {topic}`,
      ``,
      `Propose {n} questions the two of you should answer together. Draw them from`,
      `what you actually know about this, so pick questions you could contribute to.`,
      `One question per line, nothing else.`,
    ].join("\n"),
  },
  {
    id: "introCollab",
    label: "Staging think: who you are working with",
    stage: "prepare",
    mode: "collaborate",
    help:
      "Asks what this side BRINGS and where it expects to need the other, rather than where it expects to disagree. " +
      "Still forbids figures, for the same measured reason as the debate version: this pass runs without retrieval " +
      "and a pass with nothing to check against invents specifics.",
    vars: ["other", "topic", "order"],
    required: ["topic"],
    text: [
      `You are working with {other} on: {topic}`,
      ``,
      `You go {order}. Take a moment to prepare. Say what your tradition brings to`,
      `this that theirs may not, and where you expect to need them.`,
      ``,
      `Your position only, in general terms. Do not cite figures, percentages or`,
      `measurements here; those come later, once you have looked things up. Do not`,
      `start answering yet.`,
    ].join("\n"),
  },
  {
    id: "openingCollab",
    label: "First contribution instruction",
    stage: "opening",
    mode: "collaborate",
    help:
      "'Nobody has spoken yet' is kept, and it is still load-bearing, for a slightly different reason. In debate it " +
      "stopped a speaker reacting to a phantom. Here it also keeps the first round INDEPENDENT, so each side answers " +
      "from its own memory before it can be pulled toward the other's framing.",
    vars: [],
    required: [],
    text: [
      `This is your first contribution. Nobody has spoken yet, so do not react to`,
      `anyone. Say what you know that bears on the question, in your own voice,`,
      `from what you know. A few sentences.`,
    ].join("\n"),
  },
  {
    id: "rebuttalThinkCollab",
    label: "Addition think: what did they leave out",
    stage: "rebuttal",
    mode: "collaborate",
    help:
      "NEVER ASK IT TO SUMMARISE THEM. The first version of this said 'note what they have COVERED, and what you " +
      "know that they did not say', and the measured result was the think block turning into a paraphrase of the " +
      "other speaker's last turn, word for word in places. In debate the think prompt asks where they are WRONG, " +
      "which forces different words out; asking a 1B model to summarise gets you a copy. The notes had been the " +
      "clean layer in every earlier measurement, and that one clause moved the echo into them. " +
      "Saying plainly that there is nothing to add is a legitimate result and is offered explicitly, because a " +
      "think pass with nothing of its own and no way to say so will fill the space with their words.",
    vars: ["other", "question", "theirAnswer"],
    required: ["theirAnswer"],
    text: [
      `The question was: "{question}"`,
      ``,
      `{other} said:`,
      ``,
      `{theirAnswer}`,
      ``,
      `Note ONLY what you know that they did NOT say. Do not summarise or restate`,
      `their answer; you have just read it. If everything you know about this is`,
      `already in what they said, write one line saying so and nothing else.`,
      `Look anything up that you need. Do not reply to them yet.`,
    ].join("\n"),
  },
  {
    id: "rebuttalCollab",
    label: "Addition instruction",
    stage: "rebuttal",
    mode: "collaborate",
    help:
      "EXPECT MORE ECHO HERE THAN IN DEBATE, and watch the score. Disagreement forces new words; agreeing with " +
      "someone is easiest to do in their words, so removing the adversarial pressure removes what was suppressing " +
      "the copying. 'Do not restate what they already said' survives from the debate version unchanged, and it is " +
      "the load-bearing sentence in both. 'Do not tell them they are right' is the collaboration equivalent of the " +
      "reactive opener: sycophantic filler contributes nothing and costs a turn.",
    vars: ["rest"],
    required: ["rest"],
    text: [
      `Add what they missed, in your OWN words, from your own tradition. Bring`,
      `something they did not have. Do not restate what they already said, and do`,
      `not tell them they are right.`,
      ``,
      `If you have nothing to add that they did not already say, say only: {rest}`,
    ].join("\n"),
  },

  {
    id: "handoff",
    label: "Hand-off (the prefill's unfinished sentence)",
    stage: "token",
    help:
      "MUST END MID-SENTENCE. A prefill ending on a full stop invites the model to treat its own note as a " +
      "completed remark and respond to it, which is exactly what happened: openings began 'That's a solid " +
      "starting point'. An unfinished clause can only be continued.",
    vars: [],
    required: [],
    text: "So, to answer the question directly:",
  },
  {
    id: "rest",
    label: "Yield token",
    stage: "token",
    help:
      "Bare, not parenthesised, and not a sentence. Era 2 measured a real penalty for decorated sentinels, and a " +
      "model asked 'would you like to respond?' answers in prose, which cannot be parsed. Keep it to one word.",
    vars: [],
    required: [],
    text: "rest",
  },
  {
    id: "bannerConfident",
    label: "Memory banner: confident recall",
    stage: "memory",
    help:
      "WARNING, MEASURED. The binding sentence is what holds grounding at small sizes. Rewording it to hedge cost " +
      "37 points on Gemma 3 270M, halved Ternary Bonsai 1.7B, and made Qwen 0.8B invent 14 of 18 claims, while all " +
      "three confident arms reproduced their baselines exactly. Loosen this and the page fabricates.",
    vars: ["facts"],
    required: ["facts"],
    text: [
      `What you know about this:`,
      ``,
      `{facts}`,
      ``,
      `Answer using only what is above. Do not add facts of your own.`,
      `Put it in your own words as flowing prose, not a list.`,
      `Never begin with "Based on", "Here is what", or any mention of records,`,
      `context or sources. Start with the answer.`,
    ].join("\n"),
  },
  {
    id: "bannerDeliberate",
    label: "Memory banner: think passes",
    stage: "memory",
    help:
      "A think pass is not an answer, and the ordinary banner told it that it was: it ends 'Start with the answer' " +
      "directly above a prompt saying 'Do not answer yet'. The banner was winning, so notes came back as second " +
      "answers instead of as the thing the agent brings of its own. Binding sentence unchanged, answer-shaping removed.",
    vars: ["facts"],
    required: ["facts"],
    text: [
      `What you know about this:`,
      ``,
      `{facts}`,
      ``,
      `Answer using only what is above. Do not add facts of your own.`,
      `This is a private note to yourself. It is not a reply and nobody will`,
      `read it, so do not address anyone and do not write an opening line.`,
    ].join("\n"),
  },
  {
    id: "bannerHedged",
    label: "Memory banner: weak recall (NOT used by default)",
    stage: "memory",
    help:
      "Unreachable unless forced. Built to fix a real bug, then measured to make every model below the 1B tier " +
      "fabricate badly. Kept for experiments only. The likely right answer for weak recall is to WITHHOLD the " +
      "facts entirely and let the empty-sentinel path say 'I have no record of it', not to soften the words.",
    vars: ["facts"],
    required: ["facts"],
    text: [
      `Some of what came to mind, though it may not bear on the question:`,
      ``,
      `{facts}`,
      ``,
      `Use only what genuinely bears on the question. If none of it does, say`,
      `plainly that this falls outside what you remember. Do not invent`,
      `something to fill the gap, and do not stretch the notes above to fit.`,
      `Put it in your own words as flowing prose, not a list.`,
      `Never begin with "Based on", "Here is what", or any mention of records,`,
      `context or sources. Start with the answer.`,
    ].join("\n"),
  },
];

export const PROMPT_BY_ID: Record<PromptId, PromptSpec> = Object.fromEntries(
  PROMPT_SPECS.map((s) => [s.id, s]),
) as Record<PromptId, PromptSpec>;

/** What the visitor has changed. Absent means use the default. */
export type PromptOverrides = Partial<Record<PromptId, string>>;

/**
 * The overrides in force right now, set once by the UI.
 *
 * MODULE STATE, ON PURPOSE, and it is the same argument that put the
 * generation tap on `MindClient.generate` rather than on its eight callers.
 * Threading an overrides parameter through `introPrompt`, `questionPrompt`,
 * `openingInstruction`, `rebuttalThinkPrompt`, `rebuttalInstruction`,
 * `agendaPrompt` and three banners, across four files, is a list you can forget
 * to add to. And the failure when you forget is SILENT: that one prompt quietly
 * ignores what the user typed, and the panel appears to have a broken field.
 *
 * A single active set means every call site picks up an edit by construction.
 *
 * The tradeoffs, stated rather than hidden: there is one debate at a time in a
 * browser tab, so there is nothing to interleave; and prompts genuinely are
 * application-wide configuration rather than per-call arguments. An explicit
 * override parameter still wins where one is passed, which is what the tests
 * use, so nothing here can leak into a test that does not ask for it.
 */
let active: PromptOverrides = {};

/** UI only. Tests pass explicit overrides instead, so they cannot leak. */
export function setActivePrompts(ov: PromptOverrides): void {
  active = ov;
}

export function getActivePrompts(): PromptOverrides {
  return active;
}

/** The text to actually use for a prompt: explicit override, then active, then default. */
export function promptText(id: PromptId, overrides?: PromptOverrides): string {
  const explicit = overrides?.[id];
  if (explicit !== undefined && explicit !== "") return explicit;
  const live = active[id];
  if (live !== undefined && live !== "") return live;
  return PROMPT_BY_ID[id].text;
}

/** Render a prompt with its variables, honouring any override. */
export function prompt(
  id: PromptId,
  vars: Record<string, string> = {},
  overrides?: PromptOverrides,
): string {
  return render(promptText(id, overrides), vars);
}

/**
 * The stages that differ between modes, and which prompt each resolves to.
 *
 * A LOOKUP RATHER THAN A NAMING CONVENTION. Deriving the id by appending
 * "Collab" would work until the first stage that has no collaboration variant,
 * and then it would silently render a prompt that does not exist. This table
 * is exhaustive and the compiler checks it.
 *
 * `question` is deliberately absent: "prepare your answer to this specific
 * question" is the same instruction whichever way the two speakers are pointed.
 */
export const MODE_PROMPTS = {
  debate: {
    agenda: "agenda",
    intro: "intro",
    opening: "opening",
    rebuttalThink: "rebuttalThink",
    rebuttal: "rebuttal",
  },
  collaborate: {
    agenda: "agendaCollab",
    intro: "introCollab",
    opening: "openingCollab",
    rebuttalThink: "rebuttalThinkCollab",
    rebuttal: "rebuttalCollab",
  },
} as const satisfies Record<DebateMode, Record<string, PromptId>>;

export type ModeStage = keyof (typeof MODE_PROMPTS)["debate"];

/** Render the prompt for a stage, in the mode that is running. */
export function modePrompt(
  mode: DebateMode,
  stage: ModeStage,
  vars: Record<string, string> = {},
  overrides?: PromptOverrides,
): string {
  return prompt(MODE_PROMPTS[mode][stage], vars, overrides);
}

/** The prompts in play for a mode. The panel narrows to these. */
export function specsForMode(mode: DebateMode): PromptSpec[] {
  return PROMPT_SPECS.filter((s) => !s.mode || s.mode === mode);
}

/** Overrides that differ from the default, for saving and for showing a count. */
export function changedIds(overrides: PromptOverrides): PromptId[] {
  return PROMPT_SPECS.filter(
    (s) => overrides[s.id] !== undefined && overrides[s.id] !== "" && overrides[s.id] !== s.text,
  ).map((s) => s.id);
}
